<template>
  <section
    class="border-b border-b-grey-500 py-4 tablet:py-8"
    :class="{
      'group relative cursor-pointer transition-colors duration-200 hover:bg-black/80': isEditing,
    }"
    @click="$emit('click')"
  >
    <div
      v-if="isEditing"
      class="duraton-200 absolute inset-0 flex flex-col items-center justify-center opacity-0 transition-opacity group-hover:opacity-100"
    >
      <img class="mb-2" src="~/assets/images/pencil-white.svg" />

      <div class="text-uppercase text-xs font-semibold tracking-[1px] text-white">Bearbeiten</div>
    </div>

    <div class="grid-container">
      <div class="col-span-12" :class="{ 'tablet:col-start-3 tablet:col-end-11': isEditing }">
        <div class="flex flex-col justify-between gap-y-6 tablet:flex-row tablet:items-center tablet:gap-x-4">
          <div class="flex items-center">
            <JobDetailsHeaderLogo v-if="!isEditing" class="mr-4 hidden shrink-0 tablet:flex" :logo="job.companyLogoThumb" />

            <div>
              <div class="mb-2 flex items-center">
                <JobDetailsHeaderLogo v-if="!isEditing" class="mr-3 shrink-0 tablet:hidden" :logo="job.companyLogoThumb" />

                <div>
                  <div v-if="job.publishedAt" class="hidden text-xs font-semibold text-grey-600 uppercase tablet:block">
                    {{ $dayjs(job.publishedAt).locale($i18n.locale).fromNow() }}
                  </div>

                  <h1 class="text-headline-l">
                    {{ job.title }}
                  </h1>
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-5 text-[10px] font-semibold uppercase tablet:text-xs">
                <div class="flex items-center gap-x-1">
                  <img src="~/assets/images/company.svg" />

                  {{ job.companyName }}
                </div>

                <div class="flex items-center gap-x-1">
                  <img src="~/assets/images/location.svg" />

                  {{ jobLocationFull }}
                </div>

                <div class="flex items-center gap-x-1">
                  <img src="~/assets/images/clock.svg" />

                  {{ formattedJobTypes }}
                </div>

                <div class="flex items-center gap-x-1">
                  <img src="~/assets/images/grad-hat.svg" />

                  {{ formattedExperience }}
                </div>

                <div class="flex items-center gap-x-1">
                  <img src="~/assets/images/calendar-full.svg" />

                  {{ jobStartAt }}
                </div>
              </div>
            </div>
          </div>

          <slot name="actions"></slot>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'

import { type JobDetailsType, useJobDetails } from '@/helpers/jobHelpers'

import JobDetailsHeaderLogo from './JobDetailsHeaderLogo.vue'

import { useI18n } from '#imports'
import type { ExperienceEnum, JobTypeEnum } from '~/gql/jobs/schema.generated'

interface JobViewOrEditHeaderEmits {
  (e: 'click'): void
}

interface JobViewOrEditHeaderProps {
  job: JobDetailsType & {
    jobTypes?: JobTypeEnum[] | null
    companyLogoThumb?: string | null
    experiences?: ExperienceEnum[] | null
    title: string
    companyName: string
    publishedAt?: string | null
  }
  isEditing?: boolean
}

defineEmits<JobViewOrEditHeaderEmits>()

const props = defineProps<JobViewOrEditHeaderProps>()

const { t } = useI18n()
const { jobStartAt, jobLocationFull } = useJobDetails(toRef(() => props.job))

const formattedJobTypes = computed(() => (props.job.jobTypes || []).map((type) => t(`create.job_type.${type}`)).join('/'))

const formattedExperience = computed(() =>
  (props.job.experiences || []).map((experience) => t(`create.experience.${experience}`)).join(' / '),
)
</script>
